// components/shift-card.js
// ─────────────────────────────────────────────────────
// Global card component — used by Artifacts, Playbook,
// Space, Innovate, and any future feature.
// ─────────────────────────────────────────────────────
import { useState } from 'react';

function hexToRgb(hex) {
  if (!hex || !hex.startsWith('#')) return '136,136,136';
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `${r},${g},${b}`;
}

const sageBtn = {
  fontSize: 10, letterSpacing: '0.06em', padding: '4px 10px', borderRadius: 4,
  cursor: 'pointer', border: '1px solid rgba(122,158,130,0.35)',
  background: 'none', color: '#7a9e82', fontFamily: 'var(--font-serif)', transition: 'all 0.15s',
};
function sageBtnEnter(e) { e.currentTarget.style.background = 'rgba(122,158,130,0.08)'; e.currentTarget.style.borderColor = 'rgba(122,158,130,0.6)'; }
function sageBtnLeave(e) { e.currentTarget.style.background = 'none'; e.currentTarget.style.borderColor = 'rgba(122,158,130,0.35)'; }

export default function ShiftCard({
  item,
  accentColor = '#888888',
  typeLabel,
  preview,
  actionLabel,
  onAction,
  secondaryActionLabel,
  onSecondaryAction,
  secondaryActionColor,
  onSend,
  onView,
  onOpen,
  openLabel,
  hidePreview,
  onDelete,
  selectMode,
  isSelected,
  onSelect,
  fromCommunity,
}) {
  const bullets = Array.isArray(item?.summary_bullets) ? item.summary_bullets : [];
  const [saved, setSaved] = useState(false);
  const [cardHovered, setCardHovered] = useState(false);
  const accentRgb = hexToRgb(accentColor);

  function handleClick() {
    if (selectMode) { onSelect?.(item.id); return; }
    if (onView) { onView(); return; }
    onAction?.();
  }

  return (
    <div
      onClick={handleClick}
      onMouseEnter={e => {
        setCardHovered(true);
        if (selectMode) return;
        e.currentTarget.style.borderColor = `rgba(${accentRgb},0.3)`;
        e.currentTarget.style.boxShadow = `0 8px 32px rgba(${accentRgb},0.06)`;
        e.currentTarget.style.transform = 'translateY(-2px)';
      }}
      onMouseLeave={e => {
        setCardHovered(false);
        e.currentTarget.style.borderColor = isSelected ? `rgba(${accentRgb},0.4)` : '#272736';
        e.currentTarget.style.boxShadow = 'none';
        e.currentTarget.style.transform = 'translateY(0)';
      }}
      style={{
        background: '#07070a',
        border: `1px solid ${isSelected ? `rgba(${accentRgb},0.4)` : '#272736'}`,
        borderRadius: 8, overflow: 'hidden', cursor: 'pointer',
        transition: 'all 0.2s', display: 'flex', flexDirection: 'column',
      }}
    >
      {/* Top accent line */}
      <div style={{ height: 2, background: `linear-gradient(90deg,${accentColor},transparent)` }} />

      {/* Selection checkbox */}
      {selectMode && (
        <div style={{
          position: 'absolute', top: 10, right: 10, width: 16, height: 16,
          borderRadius: 3, border: `1px solid ${isSelected ? accentColor : '#2a2a2a'}`,
          background: isSelected ? accentColor : '#07070a',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10,
        }}>
          {isSelected && <svg width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="#fff" strokeWidth="2"><path d="M2 5l2.5 2.5L8 2.5"/></svg>}
        </div>
      )}

      <div style={{ padding: '14px 16px', flex: 1, display: 'flex', flexDirection: 'column' }}>

        {/* Header row — type pill + save star */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 5,
            fontSize: 9, letterSpacing: '0.1em', textTransform: 'uppercase',
            color: accentColor, background: `rgba(${accentRgb},0.08)`,
            border: `1px solid rgba(${accentRgb},0.2)`,
            padding: '3px 9px', borderRadius: 20, fontFamily: 'var(--font-serif)',
          }}>
            <div style={{ width: 5, height: 5, borderRadius: '50%', background: accentColor, flexShrink: 0, display: 'inline-block' }} />
            {typeLabel || 'artifact'}
          </div>
          {!selectMode && !fromCommunity && (
            <button onClick={e => { e.stopPropagation(); setSaved(!saved); }} style={{
              background: 'none', border: 'none', cursor: 'pointer', fontSize: 14,
              color: saved ? accentColor : '#444', padding: 2, lineHeight: 1, transition: 'color 0.15s',
            }}>{saved ? '\u2605' : '\u2606'}</button>
          )}
        </div>

        {/* Title */}
        <div style={{
          fontSize: 14, color: '#f0f0f0', fontWeight: 'bold', lineHeight: 1.4,
          marginBottom: 10, letterSpacing: '-0.01em', fontFamily: 'var(--font-serif)',
        }}>{item?.title || 'Untitled'}</div>

        {/* Core insight block */}
        {!hidePreview && (preview || item?.description) && (
          <div style={{
            padding: '10px 12px', background: `rgba(${accentRgb},0.06)`,
            borderLeft: `2px solid ${accentColor}`, borderRadius: '0 6px 6px 0', marginBottom: 10,
          }}>
            <div style={{ fontSize: 9, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#666', marginBottom: 4, fontFamily: 'var(--font-serif)' }}>Core insight</div>
            <div style={{ fontSize: 12, color: '#ccc', lineHeight: 1.6, fontStyle: 'italic' }}>{preview || item?.description}</div>
          </div>
        )}

        {/* Bullets */}
        {bullets.length > 0 && (
          <div style={{ flex: 1, marginBottom: 10 }}>
            {bullets.map((b, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 7, marginBottom: 5, fontSize: 11, color: '#888', lineHeight: 1.55 }}>
                <span style={{ color: accentColor, flexShrink: 0, marginTop: 1, fontSize: 10 }}>—</span>
                <span style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{b}</span>
              </div>
            ))}
          </div>
        )}

        {/* Community label */}
        {fromCommunity && <div style={{ color: 'var(--green)', fontSize: 11, marginBottom: 6 }}>community</div>}

        {/* Footer */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          paddingTop: 10, borderTop: '1px solid #161616', marginTop: 'auto',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {item?.created_at && (
              <span style={{ fontSize: 10, color: '#666' }}>
                {new Date(item.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
              </span>
            )}
            {item?.session_title && (
              <>
                <span style={{ color: '#333', fontSize: 10 }}>&middot;</span>
                <span style={{ fontSize: 10, color: '#555', fontStyle: 'italic' }}>&crarr; {item.session_title}</span>
              </>
            )}
          </div>

          {!selectMode && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              {onDelete && (
                <button onClick={e => { e.stopPropagation(); onDelete(); }}
                  onMouseEnter={e => { e.currentTarget.style.color = '#c46050'; e.currentTarget.style.background = 'rgba(196,96,80,0.08)'; }}
                  onMouseLeave={e => { e.currentTarget.style.color = '#666'; e.currentTarget.style.background = 'none'; }}
                  style={{
                    background: 'none', border: 'none', cursor: 'pointer', fontSize: 11, color: '#666',
                    padding: '2px 6px', borderRadius: 3, transition: 'all 0.15s',
                    fontFamily: 'var(--font-serif)', letterSpacing: '0.05em',
                    opacity: cardHovered ? 1 : 0, pointerEvents: cardHovered ? 'auto' : 'none',
                  }}>delete</button>
              )}
              {secondaryActionLabel && onSecondaryAction && (
                <button onClick={e => { e.stopPropagation(); onSecondaryAction(); }}
                  onMouseEnter={e => {
                    e.currentTarget.style.borderColor = secondaryActionColor || '#888';
                    e.currentTarget.style.color = secondaryActionColor || '#bbb';
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.borderColor = '#2a2a2a';
                    e.currentTarget.style.color = '#888';
                  }}
                  style={{
                    background: 'none',
                    border: '1px solid #2a2a2a',
                    color: '#888',
                    padding: '4px 12px',
                    borderRadius: 4,
                    fontSize: 11,
                    cursor: 'pointer',
                    fontFamily: 'var(--font-serif)',
                    letterSpacing: '0.04em',
                    transition: 'all 0.15s',
                  }}>{secondaryActionLabel}</button>
              )}
              {onSend && <button onClick={e => { e.stopPropagation(); onSend(); }} onMouseEnter={sageBtnEnter} onMouseLeave={sageBtnLeave} style={sageBtn}>Send</button>}
              {onView && <button onClick={e => { e.stopPropagation(); onView(); }} onMouseEnter={sageBtnEnter} onMouseLeave={sageBtnLeave} style={sageBtn}>View</button>}
              {onOpen && <button onClick={e => { e.stopPropagation(); onOpen(); }} onMouseEnter={sageBtnEnter} onMouseLeave={sageBtnLeave} style={sageBtn}>{openLabel || 'Open'}</button>}
              {actionLabel && !onSend && !onView && !onOpen && (
                <button onClick={e => { e.stopPropagation(); onAction?.(); }}
                  onMouseEnter={sageBtnEnter} onMouseLeave={sageBtnLeave}
                  style={sageBtn}>{actionLabel}</button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
